import { FC, useCallback, useEffect, useState } from 'react';
import { loader } from '@monaco-editor/react';
import { Avatar, Button, Card, Col, Divider, Dropdown, Menu, Row, Space, Typography } from 'antd';
import { TbDots, TbEyeCode, TbPlayerPlay, TbZoomCode } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReportComponentAccess } from '@/hooks/useNestedResourceAccess';
import {
    ReportComponentActionsDropdown,
    ReportComponentCommentButton,
    ReportComponentIcon,
    ReportComponentLockButton,
    SmallTooltip,
} from '@/shared';
import type { ReportComponentCommentResourceContext } from '@/shared/components/activity/ReportComponentCommentButton';
import { PlatformAlert } from '@/shared/components/alerts';
import { DragHandle } from '@/shared/components/drag-and-drop';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { ReportComponent } from '@/types';
import { centerComponentInMiddleofView } from '@/utilities';

import type { VantageConfig, VantageQueryResult, VantageVisualizationType } from './types';
import VantageCodeEditorModal from './VantageCodeEditorModal';
import VantageResultView from './VantageResultView';

// Ensure Monaco uses web workers
loader.init();

interface VantageProps {
    commentResource?: ReportComponentCommentResourceContext;
    component: ReportComponent;
    executeWhenReadOnly?: boolean;
    readOnly?: boolean;
}

const Vantage: FC<VantageProps> = ({ commentResource, component, executeWhenReadOnly = false, readOnly = false }) => {
    const [loading, setLoading] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [result, setResult] = useState<VantageQueryResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const { token } = useAntDesignTheme();
    const reportAccess = useReportComponentAccess(component);
    const canModify = reportAccess.canUpdate && !component.locked && !readOnly;

    const [open, setOpen] = useState(false);
    const config = (component.configuration ?? {}) as VantageConfig;
    const sql = config.sql ?? '';
    const visualization = config.visualization ?? 'table';
    const displayMode = config.displayMode ?? 'visualization';
    const limit = config.limit ?? 500;
    const resultDensity = config.preferences?.resultDensity ?? 'comfortable';
    const publicVantageResult = (
        component as ReportComponent & {
            __publicVantageResult?: { result?: VantageQueryResult; error?: string };
        }
    ).__publicVantageResult;

    const handleOpenEditor = () => {
        if (!canModify) {
            return;
        }

        setOpen(true);
    };

    const updateConfiguration = async (changes: Partial<VantageConfig>) => {
        await authorizedAxiosInstance.patch(`/api/report-components/${component.id}/configuration`, {
            configuration: {
                ...(component.configuration ?? {}),
                type: component.type,
                ...changes,
            },
        });
    };

    const executeQuery = useCallback(async () => {
        if (!sql.trim()) {
            setError('Add a Vantage query before running this component.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await authorizedAxiosInstance.post('/api/vantage-queries/execute', {
                sql,
                limit,
            });

            setResult(res.data);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? 'Unable to execute Vantage query.');
        } finally {
            setLoading(false);
        }
    }, [limit, sql]);

    useEffect(() => {
        if (publicVantageResult) {
            setResult(publicVantageResult.result ?? null);
            setError(publicVantageResult.error ?? null);
            setLoading(false);

            return;
        }

        if (readOnly && !executeWhenReadOnly) {
            return;
        }

        if (!sql.trim()) {
            setResult(null);
            setError(null);
            return;
        }

        executeQuery();
    }, [executeQuery, executeWhenReadOnly, publicVantageResult, readOnly, sql]);

    return (
        <>
            <Card
                key={component?.id}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                styles={{ body: { padding: 0, height: '100%', width: '100%' } }}
                style={{
                    height: '100%',
                    width: '100%',
                    borderRadius: token.borderRadius,
                }}
            >
                <Row style={{ padding: '1rem' }} gutter={[8, 0]} align='middle' wrap={false}>
                    {canModify && (
                        <Col className='rgl-drag-handle-slot' flex='none'>
                            <DragHandle
                                className='rgl-drag-handle'
                                label={`Move ${component.name ?? 'Vantage component'}`}
                            />
                        </Col>
                    )}

                    <Col flex='none'>
                        <ReportComponentIcon type={component.type} />
                    </Col>
                    <Col flex='none' style={{ minWidth: 0 }}>
                        <Typography.Title
                            level={5}
                            style={{
                                margin: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {component?.name ?? 'Untitled Component'}
                        </Typography.Title>
                    </Col>
                    <Col flex='auto' />
                    {!readOnly && <ReportComponentLockButton component={component} />}
                    {hovered && !readOnly && (
                        <>
                            <Col flex='none'>
                                <SmallTooltip title='Run Vantage Query' placement='topRight'>
                                    <Button loading={loading} size='small' onClick={executeQuery}>
                                        <TbPlayerPlay />
                                        {loading ? 'Running...' : 'Run'}
                                    </Button>
                                </SmallTooltip>
                            </Col>
                            <Col flex='none'>
                                <Divider type='vertical' style={{ margin: 0 }} />
                            </Col>
                            <Col flex='none'>
                                <Space.Compact>
                                    {reportAccess.canUpdate && (
                                        <Dropdown
                                            disabled={!canModify}
                                            trigger={['click']}
                                            placement='bottomRight'
                                            popupRender={() => (
                                                <DropdownMenuPanel style={{ width: 16 * 18, marginTop: '0.25rem' }}>
                                                    <div style={{ margin: '0 0.75rem', marginBottom: '0.325rem' }}>
                                                        <small style={{ color: token.colorTextTertiary }}>
                                                            Change Display Format
                                                        </small>
                                                    </div>
                                                    <Menu.Item
                                                        onClick={() => updateConfiguration({ displayMode: 'code' })}
                                                    >
                                                        Vantage Query
                                                    </Menu.Item>
                                                    <Menu.Item
                                                        onClick={() =>
                                                            updateConfiguration({ displayMode: 'visualization' })
                                                        }
                                                    >
                                                        Output Data
                                                    </Menu.Item>
                                                    <Menu.Divider />
                                                    <Menu.Item
                                                        onClick={() =>
                                                            updateConfiguration({ displayMode: 'codeAndVisualization' })
                                                        }
                                                    >
                                                        Vantage Query & Output Data
                                                    </Menu.Item>
                                                </DropdownMenuPanel>
                                            )}
                                        >
                                            <span>
                                                <SmallTooltip title='Change Display Format' placement='topRight'>
                                                    <Button size='small'>
                                                        <TbEyeCode style={{ color: token.colorTextTertiary }} />
                                                    </Button>
                                                </SmallTooltip>
                                            </span>
                                        </Dropdown>
                                    )}

                                    <SmallTooltip title='Open Vantage Query Editor' placement='topRight'>
                                        <Button disabled={!canModify} size='small' onClick={handleOpenEditor}>
                                            <TbZoomCode style={{ color: token.colorTextTertiary }} />
                                        </Button>
                                    </SmallTooltip>
                                    {reportAccess.canComment && (
                                        <ReportComponentCommentButton
                                            commentResource={commentResource}
                                            component={component}
                                            tooltipPlacement='topRight'
                                        />
                                    )}
                                    <ReportComponentActionsDropdown
                                        component={component}
                                        onRefresh={executeQuery}
                                        refreshing={loading}
                                        refreshDisabled={!sql.trim()}
                                    >
                                        <Button size='small'>
                                            <TbDots style={{ color: token.colorTextTertiary }} />
                                        </Button>
                                    </ReportComponentActionsDropdown>
                                </Space.Compact>
                            </Col>
                        </>
                    )}
                    {hovered && readOnly && commentResource && reportAccess.canComment && (
                        <Col flex='none'>
                            <ReportComponentCommentButton
                                commentResource={commentResource}
                                component={component}
                                tooltipPlacement='topRight'
                            />
                        </Col>
                    )}
                </Row>
                <div
                    style={{
                        margin: '0.5rem',
                        marginTop: 0,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        height: 'calc(100% - 4rem)',
                        backgroundColor: token.colorBgBase,
                        overflow: 'hidden',
                        backgroundImage: `radial-gradient(
                    circle at 0.125rem 0.125rem,
                    ${token.colorBorder} 0.125rem,
                    transparent 0
                    )`,
                        backgroundSize: '1.5rem 1.5rem',
                    }}
                >
                    {error ? (
                        <PlatformAlert type='error' showIcon message={error} style={{ margin: '1rem' }} />
                    ) : result || (displayMode !== 'visualization' && sql) ? (
                        <div
                            style={{
                                height: '100%',
                                minHeight: 0,
                                minWidth: 0,
                                overflow: 'hidden',
                                display: 'grid',
                                gridTemplateRows:
                                    displayMode === 'codeAndVisualization'
                                        ? 'minmax(0, 1fr) minmax(0, 1fr)'
                                        : 'minmax(0, 1fr)',
                            }}
                        >
                            {displayMode !== 'visualization' && (
                                <pre
                                    style={{
                                        margin: 0,
                                        padding: '1rem',
                                        overflow: 'auto',
                                        minHeight: 0,
                                        minWidth: 0,
                                        borderBottom:
                                            displayMode === 'codeAndVisualization'
                                                ? `1px solid ${token.colorBorder}`
                                                : undefined,
                                    }}
                                >
                                    {sql}
                                </pre>
                            )}
                            {displayMode !== 'code' && (
                                <VantageResultView
                                    result={result}
                                    visualization={visualization as VantageVisualizationType}
                                    density={resultDensity}
                                    fitContainer
                                />
                            )}
                        </div>
                    ) : (
                        <div
                            style={{
                                height: '100%',
                                width: '100%',
                                display: 'block',
                                ...centerComponentInMiddleofView,
                                textAlign: 'center',
                            }}
                        >
                            <div
                                style={{
                                    padding: '1.5rem 1rem',
                                    border: `1px solid ${token.colorBorder}`,
                                    borderRadius: token.borderRadius,
                                    backgroundColor: token.colorBgContainer,
                                }}
                            >
                                <Avatar
                                    shape='square'
                                    size={16 * 2.5}
                                    icon={<TbZoomCode size='75%' style={{ color: token.colorTextTertiary }} />}
                                    style={{ border: `1px solid ${token.colorBorder}` }}
                                />
                                <div style={{ margin: '0.75rem 0', color: token.colorTextSecondary }}>
                                    No Vantage Query Results
                                </div>
                                <div style={{ width: '20rem' }}>
                                    <small style={{ color: token.colorTextTertiary }}>
                                        Run a Vantage Query to generate insights on your investment research & due
                                        diligence data.
                                    </small>
                                </div>
                                <Button
                                    disabled={readOnly || !reportAccess.canUpdate}
                                    style={{ marginTop: '0.75rem' }}
                                    onClick={handleOpenEditor}
                                >
                                    <TbZoomCode size='1rem' />
                                    Open Vantage Query Editor
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Card>
            {!readOnly && <VantageCodeEditorModal component={component} open={open} setOpen={setOpen} />}
        </>
    );
};

export default Vantage;
